import React from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import Typography from '@material-ui/core/Typography'
import LoaderButton from 'components/atoms/LoaderButton'
import {useAuthentication} from 'components/providers/AuthenticationProvider'
import {Link as RouterLink} from 'react-router-dom'
import Link from '@material-ui/core/Link'
import Layout from 'components/layouts/WelcomePageLayout'
import Auth, {NewPasswordRequired} from 'lib/auth'
import * as Api from 'lib/api'
import {is} from 'ramda'
import TextField from '@material-ui/core/TextField'
import {makeStyles} from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import {FormHelperText} from '@material-ui/core'
import {Formik} from 'formik'
import * as Yup from 'yup'

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('formValidations.invalidEmail')
    .required('formValidations.required'),
  password: Yup.string().required('formValidations.required'),
})

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}))

function LoginForm() {
  const intl = useIntl()
  const classes = useStyles()
  const [errorMessage, setErrorMessage] = React.useState(null)
  const {signIn, setUser} = useAuthentication()

  const handleSubmit = (values, {setSubmitting}) => {
    setErrorMessage(null)
    Auth.signIn(values.email, values.password).then(result => {
      result.matchWith({
        Ok: async () => {
          const currentUser = await Auth.currentAuthenticatedUser()
          const tenant = await Api.getTenant()
          Auth.switchTenant(currentUser, tenant)
          await signIn()
        },
        Error: ({value}) => {
          setSubmitting(false)
          if (is(NewPasswordRequired, value)) {
            setUser(value.user)
          } else {
            setErrorMessage(value.message)
          }
        },
      })
    })
  }

  return (
    <Layout header={Header}>
      <Formik
        initialValues={{email: '', password: ''}}
        validationSchema={LoginSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className={classes.form} noValidate>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={intl.formatMessage({id: 'email'})}
              name="email"
              autoComplete="email"
              autoFocus
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={
                touched.email && errors.email && intl.formatMessage({id: errors.email})
              }
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={intl.formatMessage({id: 'password'})}
              type="password"
              id="password"
              autoComplete="current-password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={
                touched.password &&
                errors.password &&
                intl.formatMessage({id: errors.password})
              }
            />
            {errorMessage && (
              <FormHelperText error>
                <FormattedMessage id={`login.errors.${errorMessage}`} />
              </FormHelperText>
            )}
            <LoaderButton
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              loadingText={intl.formatMessage({id: 'login.loggingIn'})}
              isLoading={isSubmitting}
            >
              <FormattedMessage id="login.button" />
            </LoaderButton>
          </form>
        )}
      </Formik>
      <Grid container>
        <Grid item xs>
          <Link to="/login#/forgot-password" variant="body2" component={RouterLink}>
            <FormattedMessage id="login.forgotPasswordLink" />
          </Link>
        </Grid>
        <Grid item>
          <Link to="/signup" variant="body2" component={RouterLink}>
            <FormattedMessage id="login.signUpLink" />
          </Link>
        </Grid>
      </Grid>
    </Layout>
  )
}

const Header = () => (
  <Typography variant="h5" component="h1">
    <FormattedMessage id="login.greeting" />
  </Typography>
)

export default LoginForm
